import { useEffect } from "react";
import { TrendingUp, ArrowRight, ShieldCheck, Clock, PiggyBank, Briefcase, Users, UserCheck, Phone } from "lucide-react";
import LeadForm from "../components/LeadForm";
import { SectionEyebrow, FadeInSection } from "../components/SharedSections";

const TfsaInvestmentsPage = () => {
  useEffect(() => {
    document.title = "TFSA & Investment Planning BC | Mayker Financial";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "Maximize your Tax-Free Savings Account with a personalized TFSA strategy from Mayker Financial — independent advisors in BC.");
    }
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    { icon: <TrendingUp className="w-5 h-5" />, title: "Tax-free growth", desc: "Every dollar you earn inside a TFSA stays yours" },
    { icon: <ArrowRight className="w-5 h-5" />, title: "Flexible withdrawals", desc: "Access your money anytime without penalty" },
    { icon: <Clock className="w-5 h-5" />, title: "Unused room carries forward", desc: "Never lose contribution room" },
    { icon: <PiggyBank className="w-5 h-5" />, title: "Works with any investment", desc: "GICs, mutual funds, ETFs, and more" }
  ];

  const approaches = [
    { 
      title: "Conservative", 
      desc: "Capital preservation with steady, low-risk growth. Ideal for short-term goals or risk-averse investors.",
      color: "bg-blue-50 border-blue-200"
    },
    { 
      title: "Balanced", 
      desc: "A mix of growth and stability. The most popular approach for medium-term goals and retirement planning.",
      color: "bg-[#C9A84C]/10 border-[#C9A84C]/30"
    },
    { 
      title: "Growth-Oriented", 
      desc: "Maximizing long-term returns for investors with a longer timeline and higher risk tolerance.",
      color: "bg-green-50 border-green-200"
    }
  ];

  const audiences = [
    { icon: <Briefcase className="w-5 h-5" />, name: "Young professionals just starting to save" },
    { icon: <Users className="w-5 h-5" />, name: "Families building long-term wealth" },
    { icon: <ShieldCheck className="w-5 h-5" />, name: "Business owners with surplus income" },
    { icon: <UserCheck className="w-5 h-5" />, name: "Pre-retirees maximizing tax-free savings" }
  ];

  return (
    <div data-testid="tfsa-investments-page">
      {/* Hero */}
      <section className="bg-[#0C1B33] pt-28 lg:pt-32 pb-16 lg:pb-20 px-4 lg:px-12 relative">
        <div className="hero-bottom-line" />
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow light>TFSA & Investments · British Columbia</SectionEyebrow>
            <h1 className="font-playfair text-3xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-3xl">
              Grow your wealth. <em className="italic text-[#C9A84C]">Keep more of it.</em>
            </h1>
            <p className="text-base lg:text-lg text-white/60 leading-relaxed max-w-2xl mb-8">
              A Tax-Free Savings Account is one of the most powerful financial tools available to Canadians — and most people aren't using it to its full potential. We build personalized TFSA and investment strategies that align with your goals and timeline.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="#quote-form"
                className="inline-block bg-[#C9A84C] text-[#0C1B33] text-sm font-medium tracking-wide py-3.5 px-8 rounded hover:bg-[#E8CC80] transition-colors"
              >
                Book a Strategy Session →
              </a>
              <a 
                href="tel:7788361962"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium py-3.5 px-4 transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call 778-836-1962
              </a>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* TFSA Explainer */}
      <section className="bg-[#FAF8F3] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <FadeInSection>
              <SectionEyebrow>Understanding TFSAs</SectionEyebrow>
              <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-5">
                Why a TFSA is your most powerful savings tool
              </h2>
              <div className="bg-white border border-black/5 rounded-lg p-6">
                <h3 className="font-playfair text-lg font-semibold text-[#0C1B33] mb-3">What is a TFSA?</h3>
                <p className="text-[15px] text-[#6B7A99] leading-relaxed">
                  A registered account where your investments grow 100% tax-free. No tax on growth, no tax on withdrawals — ever. Contribution room accumulates every year, and any unused room carries forward indefinitely.
                </p>
              </div>
            </FadeInSection>

            <FadeInSection>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 lg:mt-0">
                {benefits.map((benefit, index) => (
                  <div key={index} className="bg-white border border-black/5 rounded-lg p-4">
                    <div className="w-9 h-9 bg-[#C9A84C]/10 rounded-lg flex items-center justify-center mb-3 text-[#C9A84C]">
                      {benefit.icon}
                    </div>
                    <h3 className="font-medium text-[#0C1B33] text-sm mb-1">{benefit.title}</h3>
                    <p className="text-xs text-[#6B7A99]">{benefit.desc}</p>
                  </div>
                ))}
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Investment Approach */}
      <section className="bg-[#0C1B33] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow light>Investment strategies</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-white leading-tight mb-10 lg:mb-12">
              How we invest your money
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {approaches.map((approach, index) => (
              <FadeInSection key={index}>
                <div className={`rounded-lg p-6 h-full border ${approach.color}`}>
                  <h3 className="font-playfair text-xl font-semibold text-[#0C1B33] mb-3">
                    {approach.title}
                  </h3>
                  <p className="text-sm text-[#6B7A99] leading-relaxed">
                    {approach.desc}
                  </p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="bg-[#F0EDE4] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeInSection>
            <SectionEyebrow>Who it's for</SectionEyebrow>
            <h2 className="font-playfair text-2xl lg:text-4xl font-semibold text-[#0C1B33] leading-tight mb-10 lg:mb-12">
              TFSA strategies for every stage
            </h2>
          </FadeInSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {audiences.map((item, index) => (
              <FadeInSection key={index}>
                <div className="bg-white border border-black/5 rounded-lg p-5 text-center h-full">
                  <div className="w-10 h-10 bg-[#C9A84C]/10 rounded-lg flex items-center justify-center mb-3 text-[#C9A84C] mx-auto">
                    {item.icon}
                  </div>
                  <p className="text-sm text-[#6B7A99]">{item.name}</p>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Form */}
      <section id="quote-form" className="bg-[#0C1B33] py-16 lg:py-20 px-4 lg:px-12">
        <div className="max-w-xl mx-auto">
          <FadeInSection>
            <div className="bg-[#FAF8F3] rounded-xl p-6 lg:p-8 card-top-accent">
              <LeadForm 
                phoneRequired
                title="Book a free TFSA strategy session"
                subtitle="Let's maximize your tax-free savings potential."
                intentOptions={[
                  { value: "Starting a TFSA", label: "Starting a TFSA" },
                  { value: "Maximizing my TFSA", label: "Maximizing my TFSA" },
                  { value: "Investment strategy review", label: "Investment strategy review" },
                  { value: "General investment planning", label: "General investment planning" },
                  { value: "Not sure — need guidance", label: "Not sure — need guidance" },
                ]}
              />
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
};

export default TfsaInvestmentsPage;
